import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import type { RemakeVersion } from '@/lib/api/amazon-listing.types'

interface RemakeVersionSelectorProps {
  versions: RemakeVersion[]
  currentRemakeId: string | undefined
  productId: string
  auditJobId: string
}

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'validation_failed', 'verification_required'])

export function RemakeVersionSelector({
  versions,
  currentRemakeId,
  productId,
  auditJobId: _auditJobId,
}: RemakeVersionSelectorProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const terminal = versions.filter(v => TERMINAL_STATUSES.has(v.status))
  if (terminal.length < 2) return null

  const currentVersion = terminal.find(v => v.id === currentRemakeId)?.version_number
    ?? terminal[0]?.version_number

  return (
    <div className="mb-6 pb-4 border-b border-neutral-200 dark:border-neutral-700">
      <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2">
        {t('amazonListing.remakeVersions')}
      </p>
      <select
        value={currentVersion ?? ''}
        onChange={(e) => {
          const v = terminal.find(ver => ver.version_number === Number(e.target.value))
          if (v) {
            const params = new URLSearchParams({ tab: 'remake', remake_id: v.id })
            navigate(`/v2/tools/product-listing-audit/${productId}?${params.toString()}`)
          }
        }}
        className="w-full max-w-xs px-3 py-2 bg-neutral-50 dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg text-sm"
      >
        {[...terminal].sort((a, b) => b.version_number - a.version_number).map(v => (
          <option key={v.id} value={v.version_number}>
            {t('amazonListing.remakeVersion', { number: v.version_number })}
            {' '}{statusLabel(t, v.status)}
            {v.created_at ? ` · ${new Date(v.created_at).toLocaleDateString()}` : ''}
          </option>
        ))}
      </select>
    </div>
  )
}

function statusLabel(t: (key: string) => string, status: string): string {
  if (status === 'completed') return ''
  return `· ${t(`remake.status.${status}`)}`
}
